import React, { useState, useEffect } from 'react';
import {
    Search,
    Loader2,
    User,
    BookOpen,
    ShoppingBag,
    DollarSign,
    Calendar,
    Package
} from 'lucide-react';
import { API_BASE_URL } from '../../config';

const ProviderOrders = ({ authToken }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await fetch(`${API_BASE_URL}/admin/provider/orders`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    const list = Array.isArray(data) ? data : (data.orders || data.order_list || []);
                    setOrders(list);
                } else {
                    const errData = await response.json().catch(() => ({}));
                    setError(errData.error || 'Failed to load orders');
                    setOrders([]);
                }
            } catch (err) {
                setError('Failed to fetch orders');
                setOrders([]);
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, [authToken]);

    const getCustomerDisplay = (o) => {
        if (!o) return '—';
        const name = o.customer_name ?? o.name;
        if (name && String(name).trim()) return String(name).trim();
        return o.customer_email ?? o.email ?? '—';
    };

    const filteredOrders = orders.filter(o => {
        const term = searchTerm.toLowerCase();
        const customer = getCustomerDisplay(o).toLowerCase();
        const item = (o.item_name || o.course_name || o.product_name || '').toLowerCase();
        const status = (o.status || '').toLowerCase();
        return customer.includes(term) || item.includes(term) || status.includes(term);
    });

    const totalRevenue = filteredOrders.reduce((sum, o) => {
        const value = o.amount ?? o.price ?? 0;
        const num = typeof value === 'string' ? Number(value.replace(/[^0-9.]/g, '')) : Number(value);
        return sum + (isNaN(num) ? 0 : num);
    }, 0);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount ?? 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        try {
            return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
        } catch (_) {
            return dateStr;
        }
    };

    const getItemTypeLabel = (o) => {
        const type = (o.item_type || o.type || '').toLowerCase();
        if (type === 'course') return 'Course';
        if (type === 'product' || type === 'artwork') return 'Product';
        return null;
    };

    return (
        <div className="space-y-8 p-1">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Orders</h2>
                    <p className="text-sm text-gray-500 mt-1">Course enrollments and product orders for your store.</p>
                </div>
            </div>
            {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">{error}</div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center space-x-4">
                    <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
                        <Package className="h-6 w-6" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Orders</p>
                        <p className="text-2xl font-black text-gray-900">{filteredOrders.length}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center space-x-4">
                    <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600">
                        <DollarSign className="h-6 w-6" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Revenue</p>
                        <p className="text-2xl font-black text-gray-900">{formatCurrency(totalRevenue)}</p>
                    </div>
                </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by customer, item name or status..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    />
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg shadow-gray-100 border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead className="bg-gray-50/50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">#</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Customer</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Item</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest text-center">Qty</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Amount</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Date</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-50">
                                {filteredOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-12 text-center text-gray-400 font-medium">
                                            No orders yet. Course enrollments and product orders will appear here.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredOrders.map((o, idx) => (
                                        <tr key={o.order_id || o.id || idx} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-600">{idx + 1}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mr-2">
                                                        <User className="h-4 w-4 text-gray-500" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{getCustomerDisplay(o)}</div>
                                                        {(o.customer_email || o.email) && (
                                                            <div className="text-xs text-gray-500">{o.customer_email ?? o.email}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    {(o.item_type || o.type) === 'course' ? (
                                                        <BookOpen className="h-4 w-4 text-indigo-500 shrink-0" />
                                                    ) : (
                                                        <ShoppingBag className="h-4 w-4 text-gray-400 shrink-0" />
                                                    )}
                                                    <span className="text-sm font-semibold text-gray-900">
                                                        {o.item_name ?? o.course_name ?? o.product_name ?? '—'}
                                                    </span>
                                                    {getItemTypeLabel(o) && (
                                                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-indigo-50 text-indigo-600 border border-indigo-100">
                                                            {getItemTypeLabel(o)}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-gray-600">
                                                {o.quantity ?? 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                                {formatCurrency(o.amount ?? o.price)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-full ${
                                                    (o.status || '').toLowerCase() === 'completed' || (o.status || '').toLowerCase() === 'paid'
                                                        ? 'bg-green-50 text-green-700 border border-green-100'
                                                        : (o.status || '').toLowerCase() === 'pending'
                                                            ? 'bg-amber-50 text-amber-700 border border-amber-100'
                                                            : 'bg-gray-50 text-gray-700 border border-gray-100'
                                                }`}>
                                                    {o.status ?? '—'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                                                {formatDate(o.date ?? o.order_date ?? o.created_at)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProviderOrders;
